import { useState } from 'react';
import type { ScoringSex } from '../../domain/users/types';
import {
  normalizeEmail,
  isValidEmail,
  isValidBodyWeight,
  isValidDisplayName,
} from '../../domain/users/types';
import './LoginPage.css';

type LoginStep = 'email' | 'create-profile';

interface ProfileFormData {
  displayName: string;
  bodyWeightLb: string;
  scoringSex: ScoringSex;
}

interface LoginPageProps {
  onLogin: (email: string) => void;
  onCreateProfile: (data: {
    email: string;
    displayName: string;
    bodyWeightLb: number;
    scoringSex: ScoringSex;
  }) => void;
  isLoading?: boolean;
  error?: string | null;
  /** If the email was checked and no profile was found, go to create step */
  emailNotFound?: boolean;
}

export function LoginPage({
  onLogin,
  onCreateProfile,
  isLoading = false,
  error = null,
  emailNotFound = false,
}: LoginPageProps) {
  const [step, setStep] = useState<LoginStep>(emailNotFound ? 'create-profile' : 'email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    displayName: '',
    bodyWeightLb: '',
    scoringSex: 'male',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError(null);

    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    onLogin(normalized);
  }

  function handleCreateProfile(e: React.FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};

    if (!isValidDisplayName(profileForm.displayName)) {
      errors.displayName = 'Display name is required.';
    }

    const weight = parseInt(profileForm.bodyWeightLb, 10);
    if (!profileForm.bodyWeightLb || isNaN(weight) || !isValidBodyWeight(weight)) {
      errors.bodyWeightLb = 'Enter a whole number between 50 and 700 lbs.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    onCreateProfile({
      email: normalizeEmail(email),
      displayName: profileForm.displayName.trim(),
      bodyWeightLb: weight,
      scoringSex: profileForm.scoringSex,
    });
  }

  function goToCreateProfile() {
    setStep('create-profile');
  }

  return (
    <div className="login">
      <div className="login__wordmark">
        <h1 className="login__title">SWOL</h1>
        <div className="login__subtitle-row">
          <span className="login__rule" />
          <span className="login__star">★</span>
          <span className="login__subtitle-text">WITH FRIENDS</span>
          <span className="login__star">★</span>
          <span className="login__rule" />
        </div>
      </div>

      {step === 'email' && (
        <form className="login__form" onSubmit={handleEmailSubmit}>
          <p className="login__intro">
            Enter your email to get started or pick up where you left off.
          </p>

          <div className="login__field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              disabled={isLoading}
            />
            {emailError && <span className="login__error">{emailError}</span>}
            {error && <span className="login__error">{error}</span>}
          </div>

          <button type="submit" className="login__submit" disabled={isLoading}>
            {isLoading ? 'Looking up…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'create-profile' && (
        <form className="login__form" onSubmit={handleCreateProfile}>
          <p className="login__intro">
            No profile found for <strong>{normalizeEmail(email)}</strong>. Let&apos;s create one!
          </p>

          <div className="login__field">
            <label htmlFor="profile-name">Display Name</label>
            <input
              id="profile-name"
              type="text"
              value={profileForm.displayName}
              onChange={(e) => setProfileForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="What should we call you?"
              autoFocus
              disabled={isLoading}
            />
            {formErrors.displayName && (
              <span className="login__error">{formErrors.displayName}</span>
            )}
          </div>

          <div className="login__field">
            <label htmlFor="profile-weight">Body Weight (lbs)</label>
            <input
              id="profile-weight"
              type="number"
              inputMode="numeric"
              value={profileForm.bodyWeightLb}
              onChange={(e) => setProfileForm((f) => ({ ...f, bodyWeightLb: e.target.value }))}
              placeholder="e.g. 180"
              min="50"
              max="700"
              step="1"
              disabled={isLoading}
            />
            {formErrors.bodyWeightLb && (
              <span className="login__error">{formErrors.bodyWeightLb}</span>
            )}
          </div>

          <div className="login__field">
            <label>Scoring Profile</label>
            <div className="login__sex-toggle">
              <button
                type="button"
                className={`login__sex-option ${profileForm.scoringSex === 'male' ? 'login__sex-option--active' : ''}`}
                onClick={() => setProfileForm((f) => ({ ...f, scoringSex: 'male' }))}
                disabled={isLoading}
              >
                Male
              </button>
              <button
                type="button"
                className={`login__sex-option ${profileForm.scoringSex === 'female' ? 'login__sex-option--active' : ''}`}
                onClick={() => setProfileForm((f) => ({ ...f, scoringSex: 'female' }))}
                disabled={isLoading}
              >
                Female
              </button>
            </div>
          </div>

          {error && <span className="login__error">{error}</span>}

          <button type="submit" className="login__submit" disabled={isLoading}>
            {isLoading ? 'Creating profile…' : 'Create Profile'}
          </button>

          <button
            type="button"
            className="login__back"
            onClick={() => setStep('email')}
            disabled={isLoading}
          >
            ← Use a different email
          </button>
        </form>
      )}

      {/* If email was checked and not found, also offer create */}
      {step === 'email' && emailNotFound && (
        <div className="login__not-found">
          <p>No profile found for that email.</p>
          <button className="login__submit login__submit--secondary" onClick={goToCreateProfile}>
            Create a New Profile
          </button>
        </div>
      )}
    </div>
  );
}
